import dayjs from 'dayjs'
import { AnyAction, combineReducers } from 'redux'
import { createSelector } from 'reselect'

import {
  ADD_JOURNAL_NOTES_RECORD,
  ADD_JOURNAL_NOTES_RECORD_FULFILLED,
  ADD_JOURNAL_NOTES_RECORD_PENDING,
  ADD_JOURNAL_NOTES_RECORD_REJECTED,
  FETCH_JOURNAL_NOTES_RECORDS,
  FETCH_JOURNAL_NOTES_RECORDS_FULFILLED,
  REMOVE_JOURNAL_NOTES_RECORD,
  REMOVE_JOURNAL_NOTES_RECORD_PENDING,
  REMOVE_JOURNAL_NOTES_RECORD_REJECTED,
  UPDATE_JOURNAL_NOTES_RECORD,
  UPDATE_JOURNAL_NOTES_RECORD_FULFILLED
} from './actionTypes'
import { createPendingCounterReducer } from './common'

import { JournalNotesRecord, JournalNotesRecordsReduxState, PromiseAction, ReduxStoreState, ThunkAction } from 'Models'
import { currentJournalDateSelector } from 'ReduxStore/journalDay'
import { coreBackendService } from 'Services'
import { Utils } from 'Utils'

// Reducer
const data = (state: JournalNotesRecord[] = [], { type, payload, meta }: AnyAction): JournalNotesRecord[] => {
  switch (type) {
    case FETCH_JOURNAL_NOTES_RECORDS_FULFILLED:
      return Utils.unique<JournalNotesRecord>([...state, ...payload])
    case ADD_JOURNAL_NOTES_RECORD_PENDING:
    case REMOVE_JOURNAL_NOTES_RECORD_REJECTED:
      return [...state, meta.data]
    case ADD_JOURNAL_NOTES_RECORD_FULFILLED: {
      return [...state, payload].filter((record: JournalNotesRecord) => record.id !== meta.data.id)
    }
    case ADD_JOURNAL_NOTES_RECORD_REJECTED:
    case REMOVE_JOURNAL_NOTES_RECORD_PENDING:
      return state.filter((record: JournalNotesRecord) => record.id !== meta.data.id)
    case UPDATE_JOURNAL_NOTES_RECORD_FULFILLED:
      return state.map((record: JournalNotesRecord) =>
        record.id === meta.data.id ? { ...record, ...meta.data } : record
      )
    default:
      return state
  }
}

// Actions
export const fetchJournalNotesRecordsAction = (date: Date): PromiseAction<JournalNotesRecord[]> => ({
  type: FETCH_JOURNAL_NOTES_RECORDS,
  payload: coreBackendService.fetchJournalNotesRecords({ date })
})

export const addJournalNotesRecordAction = (description: string, date: Date): PromiseAction<JournalNotesRecord> => {
  const tempId = `tmp-${Date.now()}`
  const tempRecord: JournalNotesRecord = {
    id: tempId,
    description,
    datetime: date
  }

  return {
    type: ADD_JOURNAL_NOTES_RECORD,
    payload: coreBackendService.addJournalNotesRecord(description, date),
    meta: {
      data: tempRecord,
      triggerDefaultSuccessToast: true
    }
  }
}

export const updateJournalNotesRecordAction = (id: string, description: string): PromiseAction<JournalNotesRecord> => ({
  type: UPDATE_JOURNAL_NOTES_RECORD,
  payload: coreBackendService.updateJournalNotesRecord(id, description),
  meta: {
    data: { id, description },
    triggerDefaultSuccessToast: true
  }
})

export const removeJournalNotesRecordAction = (id: string): ThunkAction => (dispatch, getState) => {
  const record = getState().journalNotesRecords.data.find((record: JournalNotesRecord) => record.id === id)
  if (!record) return

  return dispatch({
    type: REMOVE_JOURNAL_NOTES_RECORD,
    payload: coreBackendService.deleteJournalNotesRecord(id),
    meta: { data: record }
  })
}

// Selectors
export const journalNotesRecordsSelector = ({ journalNotesRecords }: ReduxStoreState): JournalNotesRecord[] =>
  journalNotesRecords.data

export const dayJournalNotesRecordsSelector = createSelector(
  [currentJournalDateSelector, journalNotesRecordsSelector],
  (currentJournalDate, journalNotesRecords) =>
    journalNotesRecords.filter((journalRecord) =>
      journalRecord.datetime ? dayjs(currentJournalDate).isSame(journalRecord.datetime, 'day') : false
    )
)

const asyncActionTypes = [
  FETCH_JOURNAL_NOTES_RECORDS,
  ADD_JOURNAL_NOTES_RECORD,
  UPDATE_JOURNAL_NOTES_RECORD,
  REMOVE_JOURNAL_NOTES_RECORD
]

export default combineReducers<JournalNotesRecordsReduxState>({
  data,
  pendingCounter: createPendingCounterReducer(...asyncActionTypes)
})
